import { Button, Flex, HStack, Text, useDisclosure } from "@chakra-ui/react";
import { useCallback } from "react";
import { RiFilter3Line } from "react-icons/ri";
import { useParams, useSearchParams } from "react-router-dom";
import { GenericError } from "src/components/Feedback/GenericError";
import { Loading } from "src/components/Feedback/Loading";
import { NotFound } from "src/components/Feedback/NotFound";
import { SearchInput } from "src/components/Inputs/SearchInput";
import { AdminPage } from "src/components/Layout/AdminPage";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { Breadcrumb } from "src/components/Navigation/Breadcrumb";
import { useFormTemplates } from "src/components/Providers/FormTemplateProvider";
import { AdminHeading } from "src/components/Text/AdminHeading";
import { TipTag } from "src/components/TipTag";
import { SearchAndFilterTypes } from "src/constants";
import {
  listBulkActions,
  type ListBulkActionsData,
} from "src/generated/hooks/form-service/requests";
import useCheckUserRole from "src/hooks/useCheckUserRole";
import {
  useOrderByParams,
  usePaginationParams,
} from "src/hooks/useCommonSearchParams";
import { useOrganization } from "src/hooks/useOrganization";
import { useRemoteDataRestQuery } from "src/hooks/useRemoteDataQuery";
import * as breadcrumb from "src/services/breadcrumb";
import { ActivityFilters } from "./ActivityFilters";
import { ActivityList } from "./List";
import { actionTypesMatchingSearch } from "./labels";
import { useMarkFormActivitiesViewed } from "./useUnreadFormActivities";

export const FormActivity = () => {
  const { formTemplateId } = useParams();
  useMarkFormActivitiesViewed(formTemplateId);
  const organization = useOrganization();
  const { selectedNavFormTemplate } = useFormTemplates();
  const { isAvelaAdmin } = useCheckUserRole();
  const [searchParams, setSearchParams] = useSearchParams();
  const { orderBy, setOrderBy } = useOrderByParams();
  const { pagination, setPagination } = usePaginationParams();
  const { isOpen, onToggle } = useDisclosure();

  const searchTerm = searchParams.get(SearchAndFilterTypes.Search) ?? "";
  const actionTypeFilter = searchParams.get("actionType");
  const createdByFilter = isAvelaAdmin ? searchParams.get("createdBy") : null;
  const statusFilter = searchParams.get("status");

  const effectiveActionType = (() => {
    const drawerTypes = actionTypeFilter?.split(",").filter(Boolean) ?? [];
    if (!searchTerm.trim()) {
      return actionTypeFilter || undefined;
    }

    const matched = actionTypesMatchingSearch(searchTerm);
    const types = drawerTypes.length
      ? matched.filter((type) => drawerTypes.includes(type))
      : matched;

    return types.length > 0 ? types.join(",") : null;
  })();

  const activitiesRD = useRemoteDataRestQuery({
    queryKey: [
      "bulkActions",
      formTemplateId,
      pagination.limit,
      pagination.offset,
      searchTerm,
      actionTypeFilter,
      createdByFilter,
      statusFilter,
      orderBy?.sortKey,
      orderBy?.sortType,
    ],
    queryFn: () => {
      const listQuery = {
        formTemplateId: formTemplateId ?? "",
        limit: pagination.limit,
        offset: pagination.offset,
        createdBy: createdByFilter || undefined,
        status: statusFilter || undefined,
        sortKey: orderBy?.sortKey as ListBulkActionsData["query"]["sortKey"],
        sortType: orderBy?.sortType as ListBulkActionsData["query"]["sortType"],
      };

      if (effectiveActionType === null) {
        return listBulkActions({
          query: {
            ...listQuery,
            limit: 1,
            offset: 0,
          },
        }).then((response) => ({
          bulkActions: [],
          total: 0,
          createdByOptions: response.data?.data?.createdByOptions ?? [],
        }));
      }

      return listBulkActions({
        query: {
          ...listQuery,
          actionType: effectiveActionType,
        },
      }).then((response) => response.data?.data);
    },
    enabled: !!formTemplateId,
    refetchOnMount: "always",
  });

  const handleSearch = useCallback(
    (term: string) => {
      if (!term) {
        searchParams.delete(SearchAndFilterTypes.Search);
      } else {
        searchParams.set(SearchAndFilterTypes.Search, term);
      }
      searchParams.delete(SearchAndFilterTypes.Offset);
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const handleClearFilters = useCallback(() => {
    searchParams.delete("actionType");
    searchParams.delete("createdBy");
    searchParams.delete("status");
    searchParams.delete(SearchAndFilterTypes.Offset);
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  const appliedFiltersCount = [
    actionTypeFilter,
    createdByFilter,
    statusFilter,
  ].filter(Boolean).length;

  const filterButtonStyle =
    appliedFiltersCount > 0
      ? {
          variant: "outlineActivated" as const,
          colorScheme: "primary" as const,
        }
      : { variant: "outline" as const, colorScheme: "gray" as const };

  if (!formTemplateId) {
    return <NotFound />;
  }

  return (
    <AdminPage
      breadcrumb={
        <Breadcrumb
          items={breadcrumb.form.getBreadcrumbsForActivity(
            organization,
            selectedNavFormTemplate
          )}
        />
      }
      heading={<AdminHeading title="Activity" />}
    >
      <RemoteDataView
        remoteData={activitiesRD}
        notAsked={<Loading />}
        loading={<Loading />}
        error={() => <GenericError />}
        reloading={null}
      >
        {(page) => (
          <>
            <Flex gap={4} alignItems="flex-start" pb={6}>
              <TipTag />
              <Text>
                The Activity table shows a history of form actions you’ve taken
                from the list view. Click on an action to see the applied forms.
              </Text>
            </Flex>

            <HStack pb={6}>
              <SearchInput
                onSearch={handleSearch}
                value={searchTerm}
                onClear={() => handleSearch("")}
                placeholder="Search by action type"
              />
              <Button
                {...filterButtonStyle}
                leftIcon={<RiFilter3Line />}
                onClick={onToggle}
              >
                Filter
                {appliedFiltersCount > 0 && `: ${appliedFiltersCount}`}
              </Button>
            </HStack>

            <ActivityList
              activities={page.bulkActions}
              formTemplateId={formTemplateId}
              limit={pagination.limit}
              offset={pagination.offset}
              count={page.total}
              showCreatedBy={isAvelaAdmin}
              hasActiveFilters={appliedFiltersCount > 0 || !!searchTerm}
              onFetchMore={setPagination}
              onChangeSort={setOrderBy}
              onClearFilters={() => {
                handleClearFilters();
                handleSearch("");
              }}
              isLoading={activitiesRD.isLoading() && !activitiesRD.hasData()}
            />

            <ActivityFilters
              isOpen={isOpen}
              createdByOptions={page.createdByOptions}
              showCreatedBy={isAvelaAdmin}
              onToggleFilter={onToggle}
              onClearFilters={handleClearFilters}
            />
          </>
        )}
      </RemoteDataView>
    </AdminPage>
  );
};
